import React, { useState } from "react";
import PropTypes from "prop-types";
import IfscBranchLine from "./IfscBranchLine";
import { IFSC_LENGTH, normalizeIfsc } from "../../utils/ifsc";

/**
 * IFSC code field with an inline branch lookup, in the team wizard's own
 * `.form-item` dress.
 *
 * The branch, district and state land on a line directly under the input — no
 * popup, no second screen — and the input stays editable throughout. The
 * lookup is a convenience: every failure leaves the typed code exactly where
 * it is, because a branch the service doesn't know is still a branch the
 * employee banks at.
 *
 * Onboarding collects the same code inside its own `.ob-field` markup, so it
 * uses IfscBranchLine directly rather than this.
 *
 * Defaults live in the signature, not in defaultProps: React 19 dropped
 * defaultProps for function components, and ignores it silently.
 */
export default function IfscField({
  id = "ifsc",
  name = "ifsc",
  label = "IFSC code",
  value = "",
  onChange,
  onResolved,
  placeholder = "SBIN0001234",
}) {
  const [touched, setTouched] = useState(false);
  const statusId = `${id}-status`;

  const handleChange = (e) => {
    // Upper case as they type, and never longer than a code. maxLength on the
    // input covers typing; this covers paste, autofill and IME input too.
    onChange?.(normalizeIfsc(e.target.value));
  };

  return (
    <div className="form-item ifsc-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        name={name}
        className="ifsc-input"
        type="text"
        value={normalizeIfsc(value)}
        onChange={handleChange}
        // Cleared on focus so a half-typed code is never called invalid while
        // it is still being typed.
        onFocus={() => setTouched(false)}
        onBlur={() => setTouched(true)}
        placeholder={placeholder}
        maxLength={IFSC_LENGTH}
        autoComplete="off"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        aria-describedby={statusId}
      />
      <IfscBranchLine
        id={statusId}
        value={value}
        onResolved={onResolved}
        touched={touched}
      />
    </div>
  );
}

IfscField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  /** Called once per resolved code with
   *  { ifsc, branch, district, state, bank, city }. */
  onResolved: PropTypes.func,
  placeholder: PropTypes.string,
};
